import z from "zod";

import { ErrorClassification } from "../../../sentry/classification";
import type { ErrorDetails } from "../domain";

const priceErrorTypeValues = [
  "PROPERTY_ADDRESS_NOT_FOUND",
  "INVALID_PROPERTY_ADDRESS_GEO_POINT",
  "CONTRACTOR_PROFILE_NOT_FOUND",
  "SERVICE_AREA_OR_GEOGRAPHIC_AREA_NOT_FOUND",
  "MARKET_OR_GEOGRAPHIC_AREA_NOT_FOUND",
  "PARENT_RATEBOOK_NOT_FOUND",
  "RATEBOOK_NOT_FOUND",
  "DRAFT_RATEBOOK_OR_GEOGRAPHIC_AREA_NOT_FOUND",
  "RATEBOOK_ALREADY_PUBLISHED",
  "RATEBOOK_TYPE_NOT_VALID",
  "PRICE_CHANNEL_NOT_VALID",
  "FILE_NOT_FOUND_IN_S3",
  "RATEBOOK_TYPE_IMPORT_NOT_SUPPORTED",
  "RATEBOOK_IMPORT_NOT_FOUND",
  "CPM_NOT_ASSIGNED_TO_SERVICE_AREA",
  "RATEBOOK_COPY_SOURCE_REQUIRED",
] as const;
const priceErrorTypeSchema = z.enum(priceErrorTypeValues);
const PriceErrorType = priceErrorTypeSchema.enum;
type PriceErrorType = z.infer<typeof priceErrorTypeSchema>;

export type PriceErrors =
  | { errorType: typeof PriceErrorType.PROPERTY_ADDRESS_NOT_FOUND; data: { propertyAddressId: string } }
  | {
      errorType: typeof PriceErrorType.INVALID_PROPERTY_ADDRESS_GEO_POINT;
      data: { propertyAddressId: string };
    }
  | {
      errorType: typeof PriceErrorType.CONTRACTOR_PROFILE_NOT_FOUND;
      data: { contractorProfileId: string; propertyAddressId: string };
    }
  | {
      errorType: typeof PriceErrorType.SERVICE_AREA_OR_GEOGRAPHIC_AREA_NOT_FOUND;
      data: { contractorProfileId?: string; propertyAddressId?: string; serviceAreaId?: string };
    }
  | {
      errorType: typeof PriceErrorType.MARKET_OR_GEOGRAPHIC_AREA_NOT_FOUND;
      data: { propertyAddressId?: string; marketId?: number; geographicAreaId?: string };
    }
  | {
      errorType: typeof PriceErrorType.PARENT_RATEBOOK_NOT_FOUND;
      data: { parentRatebookId: string };
    }
  | {
      errorType: typeof PriceErrorType.RATEBOOK_NOT_FOUND;
      data: { ratebookId: string };
    }
  | {
      errorType: typeof PriceErrorType.DRAFT_RATEBOOK_OR_GEOGRAPHIC_AREA_NOT_FOUND;
      data: { draftRatebookId: string };
    }
  | {
      errorType: typeof PriceErrorType.RATEBOOK_ALREADY_PUBLISHED;
      data: { draftRatebookId: string; publishedAt?: string };
    }
  | {
      errorType: typeof PriceErrorType.RATEBOOK_TYPE_NOT_VALID;
      data: { draftRatebookId: string; ratebookType: string };
    }
  | {
      errorType: typeof PriceErrorType.PRICE_CHANNEL_NOT_VALID;
      data: { draftRatebookId: string; priceChannel: string };
    }
  | {
      errorType: typeof PriceErrorType.FILE_NOT_FOUND_IN_S3;
      data: { fileId: string };
    }
  | {
      errorType: typeof PriceErrorType.RATEBOOK_TYPE_IMPORT_NOT_SUPPORTED;
      data: { ratebookType: string };
    }
  | {
      errorType: typeof PriceErrorType.RATEBOOK_IMPORT_NOT_FOUND;
      data: { ratebookImportId: string };
    }
  | {
      errorType: typeof PriceErrorType.CPM_NOT_ASSIGNED_TO_SERVICE_AREA;
      data: { geographicAreaId: string; personId: string };
    }
  | {
      errorType: typeof PriceErrorType.RATEBOOK_COPY_SOURCE_REQUIRED;
      data: { geographicAreaId: string; ratebookTypes: string[] };
    };

export const priceErrorDetails: Record<PriceErrorType, ErrorDetails> = {
  PROPERTY_ADDRESS_NOT_FOUND: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "Property address not found",
  },
  INVALID_PROPERTY_ADDRESS_GEO_POINT: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "Invalid property address geo point",
  },
  CONTRACTOR_PROFILE_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Contractor profile not found",
  },
  SERVICE_AREA_OR_GEOGRAPHIC_AREA_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Service area or geographic area not found",
  },
  MARKET_OR_GEOGRAPHIC_AREA_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Market or geographic area not found",
  },
  PARENT_RATEBOOK_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Parent ratebook not found",
  },
  RATEBOOK_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Ratebook not found",
  },
  DRAFT_RATEBOOK_OR_GEOGRAPHIC_AREA_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Draft ratebook not found",
  },
  RATEBOOK_ALREADY_PUBLISHED: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription: "Ratebook is already published",
  },
  RATEBOOK_TYPE_NOT_VALID: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "Ratebook type not valid",
  },
  PRICE_CHANNEL_NOT_VALID: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "Price channel not valid",
  },
  FILE_NOT_FOUND_IN_S3: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "File not found in S3",
  },
  RATEBOOK_TYPE_IMPORT_NOT_SUPPORTED: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "Ratebook import not supported for this type of ratebook",
  },
  RATEBOOK_IMPORT_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Ratebook import not found",
  },
  CPM_NOT_ASSIGNED_TO_SERVICE_AREA: {
    classification: ErrorClassification.AUTHORIZATION_ERROR,
    sentryDescription: "CPM is not assigned to the contractor that owns this service area",
  },
  RATEBOOK_COPY_SOURCE_REQUIRED: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "A copy source geographic area is required when creating a MARKET_DEFAULT_LABOR ratebook",
  },
};
